import type {
  ApplePayAccept,
  BuyAddonsModel,
  CartAcceptModel,
  PayPalAcceptModel,
  SkipTrialModel,
  UserChangeEmailModel,
  UserInitModel,
} from '@/common/models/api.models';
import { PAYMENT_REQUEST_TIMEOUT_MS } from '@payments/common/constants/paymentInit';

const TIMED_REQUEST = { timeout: PAYMENT_REQUEST_TIMEOUT_MS };

export function useExperimentApi() {
  const store = useKegelAnalyticStore();
  const client = useApiClient();

  async function sendExperiment(experiment: string, variation: string): Promise<void> {
    await client.post('/web-payment/experiments/', {
      web_user_uuid: store.userUuid,
      gb_experiment: experiment,
      gb_variation: variation,
    });
  }

  return { sendExperiment };
}

export function useKegelApi() {
  const {
    STRIPE_ID,
    GATEWAY_NAME,
    BUSINESS_ENTITY_ID,
    PAYPAL_GATEWAY_ID,
    PAYPAL_GATEWAY_NAME,
  } = useRuntimeConfig().public;
  const { $logger } = useNuxtApp();
  const store = useKegelAnalyticStore();
  const gatewayStore = useKegelGatewayStore();
  const client = useApiClient();
  const round = useRoundKegelApi(client);

  async function initUser(body: UserInitModel): Promise<void> {
    const experiments = store.experiments.map(e => ({ name: e.experimentId, variation: e.variation }));
    const res = await client.post('/web-payment/init-user/', { ...body, web_user_uuid: store.userUuid, experiments });
    gatewayStore.beid = res.business_entity_id;
    gatewayStore.gid = res.gateway_id;
    gatewayStore.gn = res.gateway_name;
    gatewayStore.spk = res.stripe_p_key;
    gatewayStore.pgid = res.paypal_gateway_id;
    gatewayStore.pgn = res.paypal_gateway_name;
    return res;
  }

  async function ensureLagoCredentials(): Promise<void> {
    if (gatewayStore.lagoHyperswitchPk && gatewayStore.lagoPaypalClientId) return;
    round.applyLagoCredentials(await client.get('/web-payment/config/', TIMED_REQUEST));
  }

  async function getPaymentConfig(): Promise<void> {
    const res = await client.get('/web-payment/config/');
    round.applyLagoCredentials(res);
    round.applyProvider(res);
    if (res.gateway_id) gatewayStore.gid = res.gateway_id;
    if (res.gateway_name) gatewayStore.gn = res.gateway_name;
    if (res.stripe_p_key) gatewayStore.spk = res.stripe_p_key;
    if (res.paypal_gateway_id) gatewayStore.pgid = res.paypal_gateway_id;
    if (res.paypal_gateway_name) gatewayStore.pgn = res.paypal_gateway_name;
    if (res.business_entity_id) gatewayStore.beid = res.business_entity_id;
  }

  async function changeUserEmail(body: UserChangeEmailModel): Promise<any> {
    try {
      const res = await client.post('/web-payment/change-email/', body);

      return { ok: true, data: res };
    } catch (err) {
      return { ok: false, error: err };
    }
  }

  async function paypalInit(uuid: string, amount?: number): Promise<{ link?: string; ba_token?: string }> {
    const isLago = round.isRound.value;
    if (isLago) {
      return await round.paypalInit(uuid, amount);
    }
    const paypal_gateway_id = gatewayStore.pgid || PAYPAL_GATEWAY_ID;
    return await client.post('/web-payment/init/pay-pal-payment/', { web_user_uuid: uuid, paypal_gateway_id }, TIMED_REQUEST);
  }

  async function payPalAccept(payload: PayPalAcceptModel): Promise<any> {
    if (round.isRound.value) {
      return await round.payPalAccept(payload);
    }
    payload.business_entity_id = gatewayStore.beid || BUSINESS_ENTITY_ID;
    payload.paypal_gateway_id = gatewayStore.pgid || PAYPAL_GATEWAY_ID;
    payload.paypal_gateway_name = gatewayStore.pgn || PAYPAL_GATEWAY_NAME;
    return await client.post('/web-payment/accept/pay-pal-payment', payload);
  }

  async function creditCardInit(amount: number, paymentMethodType?: string, name?: string): Promise<any> {
    const isLago = round.isRound.value;
    const payload: { [key: string]: string | number | undefined } = {
      currency_code: round.currencyCode(),
      payment_method_type: paymentMethodType || 'card',
      amount,
      web_user_uuid: store.userUuid,
    };

    if (isLago) {
      Object.assign(payload, round.cardInitFields(name));
    } else {
      payload.business_entity_id = gatewayStore.beid || BUSINESS_ENTITY_ID;
      payload.gateway_id = gatewayStore.gid || STRIPE_ID;
      payload.gateway_name = gatewayStore.gn || GATEWAY_NAME;
    }

    return await client.post('/web-payment/init/card-payment/', payload, TIMED_REQUEST);
  }

  async function sendTip(amount: number): Promise<any> {
    return await client.post('/web-payment/tip/', { web_user_uuid: store.userUuid, amount });
  }

  async function addTrialAccept(payload: CartAcceptModel | PayPalAcceptModel | ApplePayAccept): Promise<any> {
    return await client.post('/web-payment/accept/add-trial/', { ...payload, web_user_uuid: store.userUuid });
  }

  async function skipTrialAccept(payload: SkipTrialModel): Promise<any> {
    return await client.post('/web-payment/accept/skip-trial/', payload);
  }

  async function creditCardAccept(payload: CartAcceptModel | ApplePayAccept): Promise<any> {
    if (round.isRound.value) {
      return await round.creditCardAccept(payload);
    }
    payload.business_entity_id = gatewayStore.beid || BUSINESS_ENTITY_ID;
    payload.gateway_id = gatewayStore.gid || STRIPE_ID;
    payload.gateway_name = gatewayStore.gn || GATEWAY_NAME;
    return await client.post('/web-payment/accept/card-payment/', { ...payload, web_user_uuid: store.userUuid }, TIMED_REQUEST);
  }

  async function buyAddons(payload: BuyAddonsModel): Promise<any> {
    if (round.isRound.value) {
      return await round.buyAddons(payload);
    }
    payload.gateway_id = gatewayStore.gid || STRIPE_ID;
    payload.paypal_gateway_id = gatewayStore.pgid || PAYPAL_GATEWAY_ID;
    return await client.post('/web-payment/addons/', payload, TIMED_REQUEST);
  }

  async function sendFbEvent(pixelId: string, body: any): Promise<any> {
    try {
      return await client.post(`/meta/track/${pixelId}`, body);
    } catch (error: unknown) {
      $logger.note({
        category: 'facebookPixel',
        message: 'sending the conversion api event failed',
        level: 'warning',
        cause: error,
      });
    }
  }

  async function getUserData(): Promise<any> {
    try {
      const response = await client.get('/meta/ip/');
      store.userData = response?.user_data;
    } catch (error: unknown) {
      $logger.note({
        category: 'api',
        message: 'reading the ip lookup failed',
        level: 'warning',
        cause: error,
      });
    }
  }

  return {
    initUser,
    getPaymentConfig,
    ensureLagoCredentials,
    changeUserEmail,
    paypalInit,
    payPalAccept,
    creditCardInit,
    creditCardAccept,
    addTrialAccept,
    skipTrialAccept,
    buyAddons,
    sendTip,
    sendFbEvent,
    getUserData,
  };
}
